<script setup>
// core
import {useVuelidate} from '@vuelidate/core'
import {email, maxLength, helpers, requiredIf} from '@vuelidate/validators'
import {ref, watch, computed} from 'vue'

// components
import InputField from "@/components/Recurring/InputField.vue"
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue"
import {useI18n} from "vue-i18n";

// ---
const {t: $t} = useI18n()
const props = defineProps({
    loading: {
        type: Boolean,
        required: true,
        default: false
    },
    currentData: {
        type: [Object, null],
        required: true,
        default: () => {
        }
    }
})

const emit = defineEmits(["submit"])

// ---
const formData = ref({
    name: null,
    company_name: null,
    email: null
})

const companyValidator = helpers.withMessage(
    $t('validation.required'),
    requiredIf(() => !!props.currentData.company_name)
)

const rules = {
    name: {maxLength: maxLength(255)},
    company_name: {
        maxLength: helpers.withMessage($t('validation.max', {length: 255}), maxLength(255)),
        companyValidator
    },
    email: {email, maxLength: maxLength(255)}
}

const v$ = useVuelidate(rules, formData, {
    $lazy: true
})

const isDataChanged = computed(() => {
    if (!props.currentData) return false

    const fieldsToCompare = ['name', 'company_name', 'email']

    return fieldsToCompare.some(key => {
        const currentValue = props.currentData[key]
        const newValue = formData.value[key]
        return newValue !== currentValue
    })
})

const handleSubmit = async () => {
    const isFormCorrect = await v$.value.$validate()

    if (isFormCorrect) {
        const fieldsToCompare = ['name', 'company_name', 'email']
        const changedData = fieldsToCompare.reduce((acc, key) => {
            const currentValue = props.currentData[key]
            const newValue = formData.value[key] !== '' ? formData.value[key] : null
            return newValue !== currentValue ? {...acc, [key]: newValue} : acc
        }, {})

        if (Object.keys(changedData).length > 0) {
            emit('submit', changedData)
        }
    }
}

watch(() => props.currentData, (newData) => {
    if (newData && typeof newData === 'object') {
        formData.value = {
            name: newData.name ? String(newData.name) : null,
            company_name: newData.company_name ? String(newData.company_name) : null,
            email: newData.email ? String(newData.email) : null
        }
    }
}, {immediate: true})
</script>

<template>
    <form class="update-form" @submit.prevent="handleSubmit">
        <h3 class="update-form__title">
            {{ $t('profile.update_info.title') }}
        </h3>
        <div class="update-form__fields">
            <div class="update-form__item">
                <p class="update-form__item-label">{{ $t('profile.update_info.name') }}</p>
                <InputField
                    v-model="formData.name"
                    :is-focus-in-start="false"
                >
                    <span class="update-form__item-error">
                        {{
                            v$.name.$error
                                ? $t('validation.max', {length: 255})
                                : ''
                        }}
                    </span>
                </InputField>
            </div>

            <div class="update-form__item">
                <p class="update-form__item-label">{{ $t('profile.update_info.company_name') }}</p>
                <InputField
                    v-model="formData.company_name"
                    :is-focus-in-start="false"
                >
                    <div class="update-form__item-error">
                        <span
                            v-for="err in v$.company_name.$errors"
                            :key="err.$uid"
                        >
                            {{ err.$message }}
                        </span>
                    </div>
                </InputField>
            </div>

            <div class="update-form__item">
                <p class="update-form__item-label">{{ $t('profile.update_info.email') }}</p>
                <InputField
                    v-model="formData.email"
                    :is-focus-in-start="false"
                >
                    <span class="update-form__item-error">
                        {{
                            v$.email.$error
                                ? (v$.email.email.$invalid
                                        ? $t('validation.invalid_email')
                                        : $t('validation.max', {length: 255})
                                )
                                : ''
                        }}
                    </span>
                </InputField>
            </div>
        </div>

        <div class="update-form__action">
            <ButtonLoading
                :loading="loading"
                :disabled="!isDataChanged"
                type="submit"
            >
                {{ $t('profile.update_info.btn') }}
            </ButtonLoading>
        </div>
    </form>
</template>

<style scoped lang="scss">
.update-form {
    &__title {
        color: #454261;
        text-align: center;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 25px;
        text-transform: uppercase;
        margin-bottom: 10px;
    }

    &__fields {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 14px;
    }

    &__item {
        display: flex;
        flex-direction: column;
        gap: 4px;

        &-label {
            color: #404040;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 21px;
            text-transform: uppercase;
        }

        &-error {
            display: block;
            min-height: 16px;
            color: var(--danger-color);
            font-size: 12px;
            line-height: 16px;
        }
    }

    &__action {
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>
